import React from "react";
import styled from "styled-components";

interface SelectorProps {
  value: string;
  options: Array<{ key: string; label: string; description?: string }>;
  onChange: (value: string) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  disabled?: boolean;
  id?: string;
}

const Selector: React.FC<SelectorProps> = ({
  value,
  options,
  onChange,
  onPrevious,
  onNext,
  disabled = false,
  id,
}) => {
  const generatedId = React.useId();
  const baseId = id ?? generatedId;

  return (
    <SelectorWrapper id={baseId}>
      <SelectorArrowButton
        id={`${baseId}-prev`}
        onClick={onPrevious}
        disabled={disabled || !onPrevious}
        title="Previous"
      >
        <svg
          width="4"
          height="6"
          viewBox="0 0 4 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ transform: "scaleX(-1)" }}
        >
          <path
            d="M4 2.57143L4 3.42857L3 3.42857L3 4.28571L2 4.28571L2 5.14286L1 5.14286L1 6L0 6L7.15493e-08 0L1 1.19249e-08L1 0.857143L2 0.857143L2 1.71429L3 1.71429L3 2.57143L4 2.57143Z"
            fill="currentColor"
          />
        </svg>
      </SelectorArrowButton>
      <SelectorDropdown
        id={`${baseId}-dropdown`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((option) => (
          <option
            key={option.key}
            value={option.key}
            title={option.description}
          >
            {option.label}
          </option>
        ))}
      </SelectorDropdown>
      <SelectorArrowButton
        id={`${baseId}-next`}
        onClick={onNext}
        disabled={disabled || !onNext}
        title="Next"
      >
        <svg
          width="4"
          height="6"
          viewBox="0 0 4 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 2.57143L4 3.42857L3 3.42857L3 4.28571L2 4.28571L2 5.14286L1 5.14286L1 6L0 6L7.15493e-08 0L1 1.19249e-08L1 0.857143L2 0.857143L2 1.71429L3 1.71429L3 2.57143L4 2.57143Z"
            fill="currentColor"
          />
        </svg>
      </SelectorArrowButton>
    </SelectorWrapper>
  );
};

// Styled Components
const SelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #2a2a2a;
  border: 2px solid #cfc9c9;
  border-radius: 6px;
  min-width: 200px;
  position: relative;
`;

const SelectorArrowButton = styled.button`
  background: none;
  border: none;
  color: #cfc9c9;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: color 0.2s ease;
  flex-shrink: 0;
  box-sizing: border-box;
  margin: 0;

  svg {
    display: block;
    width: 8px;
    height: 10px;
  }

  &:hover:not(:disabled) {
    color: #fff;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }
`;

const SelectorDropdown = styled.select`
  flex: 1;
  padding: 0;
  background-color: transparent;
  border: none;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  color: #cfc9c9;

  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-align: center;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  outline: none;

  &:hover:not(:disabled) {
    color: #d0d0d0;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }

  option {
    background-color: #2a2a2a;
    color: #b0b0b0;
    font-family: "Dogica Pixel", monospace;
  }
`;

export default Selector;
